import { CalendarClock } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { describeError } from "@/api/errors";
import type { SchedulePreviewRequest } from "@/api/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useSchedulePreview } from "@/features/scheduler/hooks/useTaskSchedules";
import {
  presetToPayload,
  type RecurrencePreset,
} from "@/features/scheduler/lib/recurrence";
import { formatDateTime } from "@/lib/date";
import { cn } from "@/lib/utils";

interface Props {
  value: RecurrencePreset;
  onChange: (value: RecurrencePreset) => void;
}

type PresetKind = RecurrencePreset["kind"];

const KINDS: PresetKind[] = ["once", "daily", "weekly", "monthly", "cron"];

// Numeracao cron: 0=domingo ... 6=sabado.
const WEEKDAYS = [1, 2, 3, 4, 5, 6, 0];

function defaultPreset(kind: PresetKind): RecurrencePreset {
  switch (kind) {
    case "once":
      return { kind: "once", runAt: "" };
    case "daily":
      return { kind: "daily", time: "09:00" };
    case "weekly":
      return { kind: "weekly", weekdays: [1], time: "09:00" };
    case "monthly":
      return { kind: "monthly", day: 1, time: "09:00" };
    case "cron":
      return { kind: "cron", expression: "" };
  }
}

function isComplete(preset: RecurrencePreset): boolean {
  switch (preset.kind) {
    case "once":
      return Boolean(preset.runAt);
    case "weekly":
      return preset.weekdays.length > 0 && Boolean(preset.time);
    case "cron":
      return preset.expression.trim().split(/\s+/).length === 5;
    default:
      return true;
  }
}

/**
 * Editor de recorrencia: presets intuitivos (Uma vez / Diariamente /
 * Semanalmente / Mensalmente) + modo avancado com expressao cron.
 * Preview das proximas execucoes vem do backend (debounce 500ms).
 */
export function RecurrenceEditor({ value, onChange }: Props) {
  const { t } = useTranslation("scheduler");

  const timezone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone,
    [],
  );

  // Debounce do payload de preview (digitar cron nao spamma a API).
  const payload: SchedulePreviewRequest | null = isComplete(value)
    ? { ...presetToPayload(value), timezone }
    : null;
  const [debounced, setDebounced] = useState(payload);
  useEffect(() => {
    const handle = setTimeout(
      () => setDebounced(payload),
      500,
    );
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(payload)]);
  const preview = useSchedulePreview(debounced);

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label>{t("recurrence.kind")}</Label>
        <Select
          value={value.kind}
          onValueChange={(v) => onChange(defaultPreset(v as PresetKind))}
        >
          <SelectTrigger className="w-[260px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {KINDS.map((k) => (
              <SelectItem key={k} value={k}>
                {t(`recurrence.kinds.${k}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {value.kind === "once" ? (
        <div className="space-y-1.5">
          <Label htmlFor="rec-run-at">{t("recurrence.runAt")}</Label>
          <Input
            id="rec-run-at"
            type="datetime-local"
            className="w-[260px]"
            value={value.runAt}
            onChange={(e) => onChange({ ...value, runAt: e.target.value })}
          />
        </div>
      ) : null}

      {value.kind === "daily" ? (
        <TimeField
          value={value.time}
          onChange={(time) => onChange({ ...value, time })}
        />
      ) : null}

      {value.kind === "weekly" ? (
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>{t("recurrence.weekdaysLabel")}</Label>
            <div className="flex flex-wrap gap-1.5">
              {WEEKDAYS.map((d) => {
                const selected = value.weekdays.includes(d);
                return (
                  <Button
                    key={d}
                    type="button"
                    size="sm"
                    variant={selected ? "default" : "outline"}
                    className={cn("min-w-[44px]", !selected && "text-ink-500")}
                    onClick={() =>
                      onChange({
                        ...value,
                        weekdays: selected
                          ? value.weekdays.filter((x) => x !== d)
                          : [...value.weekdays, d],
                      })
                    }
                  >
                    {t(`recurrence.weekdays.${d}`)}
                  </Button>
                );
              })}
            </div>
          </div>
          <TimeField
            value={value.time}
            onChange={(time) => onChange({ ...value, time })}
          />
        </div>
      ) : null}

      {value.kind === "monthly" ? (
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="rec-day">{t("recurrence.dayOfMonth")}</Label>
            <Input
              id="rec-day"
              type="number"
              min={1}
              max={31}
              className="w-[110px]"
              value={value.day}
              onChange={(e) =>
                onChange({
                  ...value,
                  day: Math.min(31, Math.max(1, Number(e.target.value) || 1)),
                })
              }
            />
          </div>
          <TimeField
            value={value.time}
            onChange={(time) => onChange({ ...value, time })}
          />
          <p className="pb-2 text-xs text-ink-500">
            {t("recurrence.monthlyHint")}
          </p>
        </div>
      ) : null}

      {value.kind === "cron" ? (
        <div className="space-y-1.5">
          <Label htmlFor="rec-cron">{t("recurrence.cronExpression")}</Label>
          <Input
            id="rec-cron"
            className="w-[260px] font-mono"
            placeholder="0 9 * * 1-5"
            value={value.expression}
            onChange={(e) =>
              onChange({ ...value, expression: e.target.value })
            }
          />
          <p className="text-xs text-ink-500">{t("recurrence.cronHint")}</p>
        </div>
      ) : null}

      <div className="rounded-xl border border-line bg-canvas/60 px-4 py-3">
        <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-ink-500">
          <CalendarClock className="h-3.5 w-3.5" />
          {t("recurrence.preview.title")}
        </p>
        {!debounced ? (
          <p className="text-sm text-ink-500">
            {t("recurrence.preview.incomplete")}
          </p>
        ) : preview.isLoading ? (
          <p className="text-sm text-ink-500">
            {t("recurrence.preview.loading")}
          </p>
        ) : preview.isError ? (
          <p className="text-sm text-danger-600">
            {describeError(preview.error)}
          </p>
        ) : (
          <ul className="space-y-0.5 text-sm text-ink-900">
            {(preview.data?.next_runs ?? []).map((iso) => (
              <li key={iso} className="tabular-nums">
                {formatDateTime(iso)}
              </li>
            ))}
          </ul>
        )}
        <p className="mt-1.5 text-xs text-ink-500">
          {t("recurrence.timezoneNote", { timezone })}
        </p>
      </div>
    </div>
  );
}

function TimeField({
  value,
  onChange,
}: {
  value: string;
  onChange: (time: string) => void;
}) {
  const { t } = useTranslation("scheduler");
  return (
    <div className="space-y-1.5">
      <Label htmlFor="rec-time">{t("recurrence.time")}</Label>
      <Input
        id="rec-time"
        type="time"
        className="w-[140px]"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
